import { Box, Typography, useTheme } from '@mui/material';
import { CheckCircle, Info } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import FeedProgramMapping from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/fish-groups/components/feed-program-mapping';
import LoadingSpinner from 'components/loading-spinner';
import { FeedProgramMap } from 'views/bluviu-nutrition/features/scenarios-v2/types/feed-program-mapping.type';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { getActiveProducts } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenarios-get';
import { useGet } from 'hooks/use-get';
import Collapsible from 'components/collapsible';
import { useParams } from 'react-router-dom';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { PostProcessScenario } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-post-process.type';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { AxiosError } from 'axios';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FeedOption } from 'views/bluviu-nutrition/features/scenarios-v2/types/feed-active-products.type';
import { useScenarioFishGroupsState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-fish-groups-context';
import { FeedSource } from 'views/bluviu-nutrition/types/feed-source.enum';

type FeedProgramMappingListProps = {
  onMappingStateChange?: (state: { allComplete: boolean; hasError: boolean }) => void;
};

type Params = {
  globalSiteId: string;
  scenarioId: string;
};

type FeedProgramMappingFormInput = {
  feedMappings: Record<string, FeedOption | null>;
};

const FeedProgramMappingList: React.FC<FeedProgramMappingListProps> = ({ onMappingStateChange }) => {
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const [scenarioResultQueryError, setScenarioResultError] = useState(false);
  const theme = useTheme();
  const params = useParams<Params>();

  const { setFeedMappings, cageFeedPrograms } = useScenarioFishGroupsState()

  // #region FETCH DATA
  const {
    r2ScenarioCageMappingDataQuery: { data: scenarios, isLoading },
  } = useR2ScenarioState();

  const { data: feedMappingOptions, isLoading: isFeedMappingOptionsLoading } = useGet(
    ['getActiveProducts', scenarios],
    ({ queryKey }) => getActiveProducts(params.globalSiteId),
    {
      enabled: !!scenarios,
    }
  );

  const { getScenarioPostProcessQuery: { data: scenarioResult, isLoading: isScenarioResultLoading, isError: scenarioResultError, error } } = useScenarioResultState();
  // #endregion

  // #region HANDLERS
  const buildFeedProgramMappings = (scenario: PostProcessScenario | undefined | null): FeedProgramMap[] => {
    if (!scenario || typeof scenario !== 'object' || !Array.isArray((scenario as any).siteSummary)) {
      return [];
    }
    const { siteSummary } = scenario;
    const feedProgramMap = new Map<string, FeedProgramMap>();

    const selectedFeedProgramIds = cageFeedPrograms.map((cage) => cage.feedProgramId);

    siteSummary.filter((s) => selectedFeedProgramIds.includes(s.feedPlanId)).forEach((feedProgram) => {
      if (!feedProgram) return;

      const existingProgram = feedProgramMap.get(feedProgram.id);
      if (!existingProgram) {
        feedProgramMap.set(feedProgram.id, {
          feedProgramId: feedProgram.id,
          feedProgramName: feedProgram.feedPlan,
          mappedCount: 0,
          totalCount: feedProgram.feeds.length,
          mappings: feedProgram.feeds.map((feed) => ({
            id: feed.feedId,
            weight: `${feed.fishSizeStart} g`,
            bluViuNutritionFeed: feed.feedName,
            sapFeed: null,
          })),
        });
      }
    });

    return Array.from(feedProgramMap.values());
  };

  const initialMappings = useMemo(() => buildFeedProgramMappings(scenarioResult ?? {} as PostProcessScenario), [scenarioResult]);
  const [feedProgramMappings, setFeedProgramMappings] = useState<FeedProgramMap[]>(initialMappings);
  const defaultValues = useMemo<FeedProgramMappingFormInput>(
    () => ({
      feedMappings: initialMappings.reduce<Record<string, FeedOption | null>>((acc, feedProgram) => {
        feedProgram.mappings.forEach((mapping) => {
          acc[mapping.id] = null;
        });
        return acc;
      }, {}),
    }),
    [initialMappings]
  );

  const form = useForm<FeedProgramMappingFormInput>({
    defaultValues,
    mode: 'onChange',
  });

  const { control, reset, trigger } = form;
  const watchedFeedMappings = useWatch({
    control,
    name: 'feedMappings',
  }) || {};
  // #endregion

  useEffect(() => {
    setFeedProgramMappings(initialMappings);

  }, [initialMappings]);

  useEffect(() => {
    trigger(); // Trigger validation on initial load to set error states for unmapped feeds
    reset(defaultValues);
  }, [defaultValues, reset, trigger]);

  useEffect(() => {
    if (!onMappingStateChange) return;
    const allComplete = feedProgramMappings.length > 0 && feedProgramMappings.every(feedProgram =>
      feedProgram.mappings.every(m => !!watchedFeedMappings[m.id]?.id)
    );
    onMappingStateChange({
      allComplete,
      hasError: scenarioResultQueryError,
    });

    if (allComplete) {
      const siteSummary = scenarioResult?.siteSummary ?? [];

      const contextFeedMappings = feedProgramMappings.map((feedProgram) => {
        const siteFeedProgram = siteSummary.find((s) => s.id === feedProgram.feedProgramId);

        return {
          feedProgramId: String(siteFeedProgram?.feedPlanId),
          feeds: (feedProgram.mappings as any[]).map((mapping) => {
            const selectedOption = watchedFeedMappings[mapping.id];
            const originalFeed = siteFeedProgram?.feeds.find((f) => f.feedId === mapping.id);
            const feedSource = originalFeed?.feedSource ?? '';
            const isBestMix = feedSource === FeedSource.BEST_MIX;

            return {
              feedSource,
              manualFeedId: isBestMix ? null : (mapping.id ?? null),
              bestMixProductCode: isBestMix ? (mapping.id ?? '') : '',
              fishSizeStart: originalFeed?.fishSizeStart ?? 0,
              sapProductNumber: selectedOption?.id ?? '',
              sapProductName: selectedOption?.name ?? '',
            };
          }),
        };
      });

      setFeedMappings(contextFeedMappings);
    }
  }, [feedProgramMappings, watchedFeedMappings, scenarioResultQueryError, onMappingStateChange]);

  // #region RENDERS

  if (scenarioResultError) {
    snackbarDispatch({
      type: SnackbarActionTypes.ERROR_SNACKBAR,
      payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error as AxiosError },
    });
  }

  if (isLoading || isScenarioResultLoading) {
    return <LoadingSpinner />;
  }

  if (!feedProgramMappings.length) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={200}>
        <Typography color="text.secondary">
          <FormattedMessage id="general.noResults" />
        </Typography>
      </Box>
    );
  }

  // #endregion

  return (
    <FormProvider {...form}>
      <Box display="flex" flexDirection="column" gap={4}>
        <Box display="flex" flexDirection="row" gap={2} alignItems="center">
          <Info size={24} color={theme.palette.gray.dark} />
          <Typography fontSize={16} color={theme.palette.gray.dark}>
            <FormattedMessage id="bluViuNutrition.applyFeed.mapping.infoBox" />
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={1}>
          {feedProgramMappings.map((feedProgram, index) => {
            const isComplete = feedProgram.mappings.every(m => !!watchedFeedMappings[m.id]?.id);
            return (
              <Collapsible
                open={index === 0}
                hideBorder
                key={feedProgram.feedProgramId}
                backgroundColor={theme.palette.lightBlue.o25}
                label={
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography fontWeight={500}>{feedProgram.feedProgramName}</Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ color: isComplete ? theme.palette.success.main : theme.palette.gray.dark }}
                    >
                      {isComplete ? <CheckCircle size={16} /> : <Info size={16} color={theme.palette.error.main} />}
                      <Typography fontSize={14}>
                        {feedProgram.mappings.filter(m => !!watchedFeedMappings[m.id]?.id).length}/{feedProgram.mappings.length}{' '}
                        <FormattedMessage id={'bluViuNutrition.applyFeed.mapping.mappedLabel'} />
                      </Typography>
                    </Box>
                  </Box>
                }
              >
                <>
                  <FeedProgramMapping
                    key={feedProgram.feedProgramId}
                    isLoading={isFeedMappingOptionsLoading}
                    feedProgram={feedProgram}
                    feedOptions={feedMappingOptions || []}
                  />
                  {!isComplete && (
                    <Box display="flex" flexDirection="row" gap={2} alignItems="center">
                      <Info size={24} color={theme.palette.error.main} />
                      <Typography fontSize={16} color={theme.palette.error.main}>
                        <FormattedMessage id="bluViuNutrition.applyFeed.missingMapping" />
                      </Typography>
                    </Box>
                  )}
                </>
              </Collapsible>
            );
          })}
        </Box>
      </Box>
    </FormProvider>
  );
};

export default FeedProgramMappingList;
